/**
 * Insurance Checkbox Component
 * 
 * This handles the "Fietsverzeker pakket" (Bike Insurance Package) checkbox.
 * The insurance can only be added if the user has:
 * 1. Selected a minimum of ART 2 lock (look for "art 2", "art 3", etc. in product title)
 * 2. Selected a GPS tracker (look for "gps" in product name)
 * 
 * These requirements are checked in both:
 * - Virtual cart (selected extra options on the page)
 * - Real shopping cart (via Shopify Cart API)
 */

class InsuranceCheckbox {
  constructor() {
    this.checkbox = null;
    this.errorContainer = null;
    this.initialized = false;
    this.realCartItems = [];
    this.init();
  }

  init() {
    // Find the insurance checkbox container
    this.container = document.querySelector('.insurance-checkbox');
    if (!this.container) {
      console.log('InsuranceCheckbox: No insurance checkbox container found on this page');
      return;
    }

    this.checkbox = this.container.querySelector('input[type="checkbox"]');
    this.errorContainer = this.container.querySelector('.insurance-checkbox__error');
    this.variantIdInput = this.container.querySelector('input[name="insurance_variant_id"]');
    
    // Debug logging
    console.log('InsuranceCheckbox: Found container', this.container);
    console.log('InsuranceCheckbox: Checkbox element', this.checkbox);
    console.log('InsuranceCheckbox: Variant ID input', this.variantIdInput);
    console.log('InsuranceCheckbox: Variant ID value', this.variantIdInput?.value);
    
    // Also check the data attribute on the container
    const containerVariantId = this.container.dataset.variantId;
    console.log('InsuranceCheckbox: Container data-variant-id', containerVariantId);
    
    if (!this.checkbox) {
      console.warn('InsuranceCheckbox: Checkbox not found');
      return;
    }

    // Load real cart items
    this.loadRealCart();

    // Listen for checkbox changes
    this.checkbox.addEventListener('change', (e) => this.onCheckboxChange(e));

    // Listen for extra option selections to update validation state
    document.addEventListener('click', (e) => {
      if (e.target.closest('.extra-option-card__price-button') || e.target.closest('.extra-option-card')) {
        // Small delay to allow the selection to be processed
        setTimeout(() => this.updateValidationState(), 100);
      }
    });

    // Also listen for expanded card add buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-expanded-add]')) {
        setTimeout(() => this.updateValidationState(), 100);
      }
    });

    // Initial validation state
    this.updateValidationState();

    this.initialized = true;
    console.log('InsuranceCheckbox: Initialized');
  }

  async loadRealCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.realCartItems = cart.items || [];
      console.log('InsuranceCheckbox: Loaded real cart', this.realCartItems.length, 'items');
      this.updateValidationState();
    } catch (e) {
      console.error('InsuranceCheckbox: Failed to load cart', e);
      this.realCartItems = [];
    }
  }

  /**
   * Check if a product title contains a valid ART level (2 or higher)
   * Looks for patterns like "ART 2", "Art-2", "ART2", "Art 3", etc.
   */
  hasValidArtLevel(title) {
    if (!title) return false;
    
    const lowerTitle = title.toLowerCase();
    
    // Look for "art" followed by a number 2 or higher
    // Match patterns: "art 2", "art-2", "art2", "art 3", "art 4", "art 5", etc.
    const artMatch = lowerTitle.match(/art[\s\-]*(\d+)/i);
    
    if (artMatch) {
      const artLevel = parseInt(artMatch[1], 10);
      return artLevel >= 2;
    }
    
    return false;
  }

  /**
   * Check if a product title contains "gps"
   */
  hasGps(title) {
    if (!title) return false;
    return title.toLowerCase().includes('gps');
  }

  /**
   * Get selected extra options from the virtual cart (on-page selections)
   */
  getVirtualCartItems() {
    const selectedCards = document.querySelectorAll('.extra-option-card.is-selected');
    const items = [];
    
    selectedCards.forEach(card => {
      const titleEl = card.querySelector('.extra-option-card__title');
      const title = titleEl ? titleEl.textContent.trim() : '';
      
      items.push({
        title: title,
        source: 'virtual'
      });
    });
    
    return items;
  }

  /**
   * Check requirements across both virtual and real cart
   * Returns { hasArt2: boolean, hasGps: boolean, missingItems: string[] }
   */
  checkRequirements() {
    const virtualItems = this.getVirtualCartItems();
    const realItems = this.realCartItems;
    
    let hasArt2 = false;
    let hasGps = false;
    
    // Check virtual cart
    virtualItems.forEach(item => {
      if (this.hasValidArtLevel(item.title)) {
        hasArt2 = true;
      }
      if (this.hasGps(item.title)) {
        hasGps = true;
      }
    });
    
    // Check real cart
    realItems.forEach(item => {
      const title = item.title || item.product_title || '';
      if (this.hasValidArtLevel(title)) {
        hasArt2 = true;
      }
      if (this.hasGps(title)) {
        hasGps = true;
      }
    });
    
    // Compile missing items
    const missingItems = [];
    if (!hasArt2) {
      missingItems.push('een ART 2 slot of hoger');
    }
    if (!hasGps) {
      missingItems.push('een GPS tracker');
    }
    
    return {
      hasArt2,
      hasGps,
      meetsRequirements: hasArt2 && hasGps,
      missingItems
    };
  }

  /**
   * Update the visual validation state
   */
  updateValidationState() {
    const requirements = this.checkRequirements();
    
    if (requirements.meetsRequirements) {
      // Hide error, allow checkbox
      this.hideError();
      this.checkbox.disabled = false;
      this.container.classList.remove('is-disabled');
      this.container.classList.add('is-available');
    } else {
      // If checkbox was checked, uncheck it
      if (this.checkbox.checked) {
        this.checkbox.checked = false;
        this.showError(requirements.missingItems);
      }
      // Don't disable the checkbox - let users click to see the error
      this.container.classList.remove('is-available');
    }
  }

  /**
   * Handle checkbox change event
   */
  onCheckboxChange(e) {
    const requirements = this.checkRequirements();
    
    if (e.target.checked && !requirements.meetsRequirements) {
      // User tried to check but requirements not met
      e.target.checked = false;
      this.showError(requirements.missingItems);
      return;
    }
    
    if (e.target.checked) {
      // Requirements met, checkbox is checked
      this.hideError();
      this.container.classList.add('is-selected');
      
      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('insuranceSelected', {
        detail: {
          selected: true,
          variantId: this.variantIdInput?.value
        }
      }));
    } else {
      // Checkbox unchecked
      this.container.classList.remove('is-selected');
      this.hideError();
      
      document.dispatchEvent(new CustomEvent('insuranceSelected', {
        detail: {
          selected: false,
          variantId: null
        }
      }));
    }
  }

  /**
   * Show error message
   */
  showError(missingItems) {
    if (!this.errorContainer) return;
    
    let message = 'Om de fietsverzekering toe te voegen heb je ';
    
    if (missingItems.length === 1) {
      message += missingItems[0] + ' nodig.';
    } else if (missingItems.length === 2) {
      message += missingItems[0] + ' en ' + missingItems[1] + ' nodig.';
    }
    
    this.errorContainer.textContent = message;
    this.errorContainer.style.display = 'block';
    this.container.classList.add('has-error');
    
    // Animate the error message
    this.errorContainer.animate([
      { opacity: 0, transform: 'translateY(-5px)' },
      { opacity: 1, transform: 'translateY(0)' }
    ], {
      duration: 200,
      easing: 'ease-out'
    });
  }

  /**
   * Hide error message
   */
  hideError() {
    if (!this.errorContainer) return;
    
    this.errorContainer.style.display = 'none';
    this.container.classList.remove('has-error');
  }

  /**
   * Check if insurance is selected
   */
  isSelected() {
    const selected = this.checkbox?.checked || false;
    console.log('InsuranceCheckbox: isSelected() called, returning:', selected);
    return selected;
  }

  /**
   * Get the insurance variant ID
   */
  getVariantId() {
    // Try multiple sources for the variant ID
    let variantId = this.variantIdInput?.value || null;
    
    // Fallback: check the data attribute on checkbox
    if (!variantId && this.checkbox) {
      variantId = this.checkbox.dataset.variantId || null;
    }
    
    // Fallback: check the container data attribute
    if (!variantId && this.container) {
      variantId = this.container.dataset.variantId || null;
    }
    
    console.log('InsuranceCheckbox: getVariantId() called, returning:', variantId);
    return variantId;
  }
}

// Initialize on page load
function initInsuranceCheckbox() {
  window.insuranceCheckbox = new InsuranceCheckbox();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInsuranceCheckbox);
} else {
  initInsuranceCheckbox();
}

// Re-initialize on section load
document.addEventListener('shopify:section:load', () => {
  initInsuranceCheckbox();
});

